import type { ZodObject } from 'zod'
import type { TLeadApplicationWizardDataPath } from '../types/lead-application'
import type { TValidationOptions } from '~~/app/composables/useZodFormValidation'

export const APPLY_FLOWS = {
  business: {
    intents: [
      'email',
      'buying-model',
      'travelers',
      'sporadic-travel',
      'review',
    ],
    stepOptions: null,
  },
  sell: {
    intents: ['reseller', 'api', 'brand', 'unsure'],
    stepOptions: [
      'email',
      'review',
      'connectivity',
      'volume',
      'platform',
      'channel',
      'stage',
      'customers',
    ],
  },
  promote: {
    intents: ['affiliate', 'influencer'],
    stepOptions: ['email', 'review'],
  },
} as const

export type TValidIntent = keyof typeof APPLY_FLOWS
export type TValidIntentOption<T extends TValidIntent> =
  (typeof APPLY_FLOWS)[T]['intents'][number]
export type TValidStepOption<T extends TValidIntent> =
  (typeof APPLY_FLOWS)[T]['stepOptions'] extends readonly string[]
    ? (typeof APPLY_FLOWS)[T]['stepOptions'][number]
    : never

export type TAnyIntent = TValidIntent
export type TAnyIntentOption =
  (typeof APPLY_FLOWS)[TAnyIntent]['intents'][number]
export type TAnyStepOption = {
  [
    K in TValidIntent
  ]: (typeof APPLY_FLOWS)[K]['stepOptions'] extends readonly string[]
    ? (typeof APPLY_FLOWS)[K]['stepOptions'][number]
    : null
}[TValidIntent]

export type TAdvancedLink = {
  to: string
  title?: string
  subTitle?: string
  imgIconFile?: string
  iconString?: string
  meta?: Record<string, string | number | boolean>
}

export type TFormSelectionListItem = {
  value: string
  title: string
  subTitle?: string
}

export type TLeadApplicationWizardStep = {
  component: string
  validationSchema?: ZodObject
  validationMode?: TValidationOptions['mode']
  routePath?: string
  optionsSelection?: {
    builderModelEntryName: string
    fieldToUpdate: TLeadApplicationWizardDataPath
  }
}

export type TLeadApplicationFlow = Record<number, TLeadApplicationWizardStep>

export type TSellOption = TValidIntentOption<'sell'>

type TLeadApplicationWizardMap = {
  business: TLeadApplicationFlow
  promote: TLeadApplicationFlow
  sell: {
    shared: TLeadApplicationFlow
    byOption: Record<TSellOption, TLeadApplicationFlow>
  }
}

export const leadApplicationWizardBaseReviewFormValidationSchema =
  createMultiDynamicSchema<TLeadApplicationWizardDataPath>([
    { path: 'firstName', schema: requiredString('First name') },
    { path: 'lastName', schema: requiredString('Last name') },
    {
      // Required, and capped at 100 chars to match the BE endpoint limit.
      path: 'businessFlowData.companyName',
      schema: requiredString('Company name', 100),
    },
    {
      path: 'businessFlowData.websiteUrl',
      schema: createWebUrlSchema({
        requiredMessage: 'Website URL is required',
        invalidMessage: 'Invalid website URL format',
        optional: true,
      }),
    },
    {
      path: 'industryNicheId',
      schema: requiredNumber('Industry', 'Industry is required'),
    },
    {
      path: 'countryId',
      schema: requiredNumber('Country', 'Country is required'),
    },
    {
      path: 'businessFlowData.yearlyRevenue',
      schema: requiredString('Yearly revenue (USD)'),
    },
    {
      path: 'businessFlowData.employeeCount',
      schema: requiredString('Employee count'),
    },
  ])

export const leadApplicationWizardMap: TLeadApplicationWizardMap = {
  business: {
    1: { component: 'IntentOptionStep' },
    2: {
      component: 'PersonalDetailsStep',
      validationSchema: emailValidationSchema,
      validationMode: 'lazy',
      routePath: '/apply/business/email',
    },
    3: {
      component: 'BusinessOptionSelection',
      routePath: '/apply/business/buying-model',
      validationSchema: createMultiDynamicSchema([
        {
          path: 'businessFlowData.esimUsageSelection',
          schema: requiredString('Business model selection'),
        },
      ]),
      validationMode: 'eager',
      optionsSelection: {
        builderModelEntryName: 'LeadApplication:Business:TeamEsimUsage',
        fieldToUpdate: 'businessFlowData.esimUsageSelection',
      },
    },
    4: {
      component: 'BusinessOptionSelection',
      routePath: '/apply/business/travelers',
      validationSchema: createMultiDynamicSchema([
        {
          path: 'businessFlowData.estimatedAnualTravellerCountSelection',
          schema: requiredString('Estimated annual traveller count selection'),
        },
      ]),
      validationMode: 'eager',
      optionsSelection: {
        builderModelEntryName:
          'LeadApplication:Business:EstimatedAnnualTravellerCount',
        fieldToUpdate: 'businessFlowData.estimatedAnualTravellerCountSelection',
      },
    },
    5: {
      component: 'BusinessOptionSelection',
      routePath: '/apply/business/sporadic-travel',
      validationSchema: createMultiDynamicSchema([
        {
          path: 'businessFlowData.certainTeamTravelTimeQuestionSelection',
          schema: requiredString('Certain team travel time question selection'),
        },
      ]),
      validationMode: 'eager',
      optionsSelection: {
        builderModelEntryName:
          'LeadApplication:Business:YearlyTeamCertainTravelTimes',
        fieldToUpdate:
          'businessFlowData.certainTeamTravelTimeQuestionSelection',
      },
    },
    6: {
      component: 'ReviewStep',
      routePath: '/apply/business/review',
      validationSchema: leadApplicationWizardBaseReviewFormValidationSchema,
      validationMode: 'lazy',
    },
  },
  promote: {
    1: { component: 'IntentOptionStep' },
    2: { component: 'IntentOptionStep' },
    3: {
      component: 'PersonalDetailsStep',
      validationSchema: createMultiDynamicSchema([
        {
          path: 'email',
          schema: requiredString('Email'),
        },
        {
          path: 'firstName',
          schema: requiredString('First name'),
        },
        {
          path: 'lastName',
          schema: requiredString('Last name'),
        },
      ]),
      validationMode: 'lazy',
      routePath: '/apply/promote/influencer/email',
    },
    4: {
      component: 'PromoteReviewStep',
      routePath: '/apply/promote/influencer/review',
      validationSchema: createMultiDynamicSchema([
        {
          path: 'industryNicheId',
          schema: requiredNumber('Industry', 'Industry is required'),
        },
        {
          path: 'countryId',
          schema: requiredNumber('Country', 'Country is required'),
        },
      ]),
      validationMode: 'lazy',
    },
  },
  sell: {
    shared: {
      1: { component: 'IntentOptionStep' },
      2: { component: 'IntentOptionStep' },
    },
    byOption: {
      reseller: {
        3: {
          component: 'PersonalDetailsStep',
          routePath: '/apply/sell/reseller/email',
          validationSchema: emailValidationSchema,
          validationMode: 'lazy',
        },
        4: {
          component: 'BusinessOptionSelection',
          routePath: '/apply/sell/reseller/volume',
          validationSchema: createMultiDynamicSchema([
            {
              path: 'sellFlowData.customersServedMonthly',
              schema: requiredString('Served monthly customers selection'),
            },
          ]),
          validationMode: 'eager',
          optionsSelection: {
            builderModelEntryName:
              'LeadApplication:Sell:CustomersServedMonthly',
            fieldToUpdate: 'sellFlowData.customersServedMonthly',
          },
        },
        5: {
          component: 'BusinessOptionSelection',
          routePath: '/apply/sell/reseller/channel',
          validationSchema: createMultiDynamicSchema([
            {
              path: 'sellFlowData.primarySalesChannel',
              schema: requiredString('Primary sales channel selection'),
            },
          ]),
          validationMode: 'eager',
          optionsSelection: {
            builderModelEntryName: 'LeadApplication:Sell:PrimarySalesChannel',
            fieldToUpdate: 'sellFlowData.primarySalesChannel',
          },
        },
        6: {
          component: 'BusinessOptionSelection',
          routePath: '/apply/sell/reseller/customers',
          validationSchema: createMultiDynamicSchema([
            {
              path: 'sellFlowData.primaryCustomerTargetGroup',
              schema: requiredString('Primary customer target group selection'),
            },
          ]),
          validationMode: 'eager',
          optionsSelection: {
            builderModelEntryName: 'LeadApplication:Sell:PrimaryCustomers',
            fieldToUpdate: 'sellFlowData.primaryCustomerTargetGroup',
          },
        },
        7: {
          component: 'ReviewStep',
          routePath: '/apply/sell/reseller/review',
          validationSchema: leadApplicationWizardBaseReviewFormValidationSchema,
          validationMode: 'lazy',
        },
      },
      brand: {
        3: {
          component: 'PersonalDetailsStep',
          routePath: '/apply/sell/brand/email',
          validationSchema: emailValidationSchema,
          validationMode: 'lazy',
        },
        4: {
          component: 'BusinessOptionSelection',
          routePath: '/apply/sell/brand/volume',
          validationSchema: createMultiDynamicSchema([
            {
              path: 'sellFlowData.customersServedMonthly',
              schema: requiredString('Served monthly customers selection'),
            },
          ]),
          validationMode: 'eager',
          optionsSelection: {
            builderModelEntryName:
              'LeadApplication:Sell:CustomersServedMonthly',
            fieldToUpdate: 'sellFlowData.customersServedMonthly',
          },
        },
        5: {
          component: 'BusinessOptionSelection',
          routePath: '/apply/sell/brand/channel',
          validationSchema: createMultiDynamicSchema([
            {
              path: 'sellFlowData.primarySalesChannel',
              schema: requiredString('Primary sales channel selection'),
            },
          ]),
          validationMode: 'eager',
          optionsSelection: {
            builderModelEntryName: 'LeadApplication:Sell:PrimarySalesChannel',
            fieldToUpdate: 'sellFlowData.primarySalesChannel',
          },
        },
        6: {
          component: 'BusinessOptionSelection',
          routePath: '/apply/sell/brand/customers',
          validationSchema: createMultiDynamicSchema([
            {
              path: 'sellFlowData.primaryCustomerTargetGroup',
              schema: requiredString('Primary customer target group selection'),
            },
          ]),
          validationMode: 'eager',
          optionsSelection: {
            builderModelEntryName: 'LeadApplication:Sell:PrimaryCustomers',
            fieldToUpdate: 'sellFlowData.primaryCustomerTargetGroup',
          },
        },
        7: {
          component: 'ReviewStep',
          routePath: '/apply/sell/brand/review',
          validationSchema: leadApplicationWizardBaseReviewFormValidationSchema,
          validationMode: 'lazy',
        },
      },
      api: {
        3: {
          component: 'PersonalDetailsStep',
          routePath: '/apply/sell/api/email',
          validationSchema: emailValidationSchema,
          validationMode: 'lazy',
        },
        4: {
          component: 'BusinessOptionSelection',
          routePath: '/apply/sell/api/platform',
          validationSchema: createMultiDynamicSchema([
            {
              path: 'sellFlowData.integrationPlatformType',
              schema: requiredString('Integration platform selection'),
            },
          ]),
          validationMode: 'eager',
          optionsSelection: {
            builderModelEntryName: 'LeadApplication:Sell:PlatformIntegration',
            fieldToUpdate: 'sellFlowData.integrationPlatformType',
          },
        },
        5: {
          component: 'BusinessOptionSelection',
          routePath: '/apply/sell/api/stage',
          validationSchema: createMultiDynamicSchema([
            {
              path: 'sellFlowData.integrationPlatformStatus',
              schema: requiredString('Integration platform stage selection'),
            },
          ]),
          validationMode: 'eager',
          optionsSelection: {
            builderModelEntryName: 'LeadApplication:Sell:PlatformStage',
            fieldToUpdate: 'sellFlowData.integrationPlatformStatus',
          },
        },
        6: {
          component: 'BusinessOptionSelection',
          routePath: '/apply/sell/api/volume',
          validationSchema: createMultiDynamicSchema([
            {
              path: 'sellFlowData.customersServedMonthly',
              schema: requiredString('Served monthly customers selection'),
            },
          ]),
          validationMode: 'eager',
          optionsSelection: {
            builderModelEntryName:
              'LeadApplication:Sell:CustomersServedMonthly',
            fieldToUpdate: 'sellFlowData.customersServedMonthly',
          },
        },
        7: {
          component: 'ReviewStep',
          routePath: '/apply/sell/api/review',
          validationSchema: leadApplicationWizardBaseReviewFormValidationSchema,
          validationMode: 'lazy',
        },
      },
      unsure: {
        3: {
          component: 'PersonalDetailsStep',
          routePath: '/apply/sell/unsure/email',
          validationSchema: emailValidationSchema,
          validationMode: 'lazy',
        },
        4: {
          component: 'BusinessOptionSelection',
          routePath: '/apply/sell/unsure/volume',
          validationSchema: createMultiDynamicSchema([
            {
              path: 'sellFlowData.customersServedMonthly',
              schema: requiredString('Served monthly customers selection'),
            },
          ]),
          validationMode: 'eager',
          optionsSelection: {
            builderModelEntryName:
              'LeadApplication:Sell:CustomersServedMonthly',
            fieldToUpdate: 'sellFlowData.customersServedMonthly',
          },
        },
        5: {
          component: 'BusinessOptionSelection',
          routePath: '/apply/sell/unsure/channel',
          validationSchema: createMultiDynamicSchema([
            {
              path: 'sellFlowData.primarySalesChannel',
              schema: requiredString('Primary sales channel selection'),
            },
          ]),
          validationMode: 'eager',
          optionsSelection: {
            builderModelEntryName: 'LeadApplication:Sell:PrimarySalesChannel',
            fieldToUpdate: 'sellFlowData.primarySalesChannel',
          },
        },
        6: {
          component: 'BusinessOptionSelection',
          routePath: '/apply/sell/unsure/customers',
          validationSchema: createMultiDynamicSchema([
            {
              path: 'sellFlowData.primaryCustomerTargetGroup',
              schema: requiredString('Primary customer target group selection'),
            },
          ]),
          validationMode: 'eager',
          optionsSelection: {
            builderModelEntryName: 'LeadApplication:Sell:PrimaryCustomers',
            fieldToUpdate: 'sellFlowData.primaryCustomerTargetGroup',
          },
        },
        7: {
          component: 'ReviewStep',
          routePath: '/apply/sell/unsure/review',
          validationSchema: leadApplicationWizardBaseReviewFormValidationSchema,
          validationMode: 'lazy',
        },
      },
    },
  },
}

export const resolveLeadApplicationFlow = (
  intent: TValidIntent | undefined,
  option: TAnyIntentOption | undefined
): TLeadApplicationFlow | undefined => {
  if (!intent) return

  if (intent === 'sell') {
    const { shared, byOption } = leadApplicationWizardMap.sell
    return option ? { ...shared, ...byOption[option as TSellOption] } : shared
  }

  return leadApplicationWizardMap[intent]
}
